import { createElement, useRef, useEffect, useState } from "react";
import { PuzzleType } from "sockapi/puzzle-data";
import { PuzzleContentType } from "sockapi/scope-specs/puzzle-content-spec";
import { ROUND_IDS } from "sockapi/scope-specs/round-spec";
import { Spinner } from "components/Loading";
import {
  PuzzleContentRenderContext,
  usePuzzleContent,
} from "components/PuzzleContent/usePuzzleContent";
import { useRound } from "stores/NavStore";
import { useAccessiblePuzzles } from "stores/ScopeStateStore";
import "./PuzzleContentDisplay.css";

// HACK: Various pieces assume a fixed height, but that interferes
// with overflow detection. There is unlikely to be a general solution
// for this, so limit overflow detection to only rounds with truncated
// pieces. These rounds should not have any pieces that depend on a
// fixed height.
const ROUNDS_WITH_TRUNCATED_PIECES = [ROUND_IDS.atoll, ROUND_IDS.site];

export function PuzzleContentDisplayPreloaded(props: {
  renderCtx: PuzzleContentRenderContext;
  contentType?: PuzzleContentType;
  fontSize?: string;
}) {
  const {
    renderCtx: { elem, puzName, components, props: elemProps },
    contentType = PuzzleContentType.PUZZLE,
    fontSize,
  } = props;
  const [overflows, setOverflows] = useState(false);
  const round = useRound();
  const isTruncationEnabled =
    round !== null && ROUNDS_WITH_TRUNCATED_PIECES.includes(round.roundId);
  const type = useAccessiblePuzzles()?.[puzName]?.type ?? PuzzleType.PUZZLE;
  const usePieceRendering =
    (type === PuzzleType.PUZZLE || type === PuzzleType.PIECE) &&
    contentType === PuzzleContentType.PUZZLE;

  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current === null) return;
    if (innerRef.current === null) return;
    if (!isTruncationEnabled) return;
    if (!usePieceRendering) return;
    const el = ref.current;
    const innerEl = innerRef.current;
    const checkOverflow = () => {
      // HACK: allow some overflow to account for imprecision.
      setOverflows(el.scrollHeight > el.clientHeight + 4);
    };
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(innerEl);
    checkOverflow();
    return () => resizeObserver.disconnect();
  }, [isTruncationEnabled, usePieceRendering]);

  if (!usePieceRendering)
    return (
      <div className="puzzle-content clipboard-container">
        {createElement(elem, {
          components,
          ...elemProps,
        })}
      </div>
    );

  return (
    <div className="puzzle-content-outer" style={{ fontSize }}>
      <div className="puzzle-content clipboard-container" ref={ref}>
        <div
          className="puzzle-content-inner"
          ref={innerRef}
          style={{
            height: !isTruncationEnabled ? "100%" : undefined,
          }}
        >
          {createElement(elem, {
            components,
            ...elemProps,
          })}
        </div>
      </div>
      {overflows ? (
        <>
          <div className="overflows-bg" />
          <div className="overflows-msg">
            (Piece truncated; expand to see full contents)
          </div>
        </>
      ) : null}
    </div>
  );
}

function PuzzleContentDisplay(props: {
  puzName: string;
  contentType?: PuzzleContentType;
  isPreview?: boolean;
  fontSize?: string;
}) {
  const {
    contentType = PuzzleContentType.PUZZLE,
    puzName,
    isPreview,
    fontSize,
  } = props;
  const { renderCtx } = usePuzzleContent(puzName, contentType, isPreview);
  if (renderCtx === null) return <Spinner inline />;
  return (
    <PuzzleContentDisplayPreloaded
      renderCtx={renderCtx}
      contentType={contentType}
      fontSize={fontSize}
    />
  );
}

export default PuzzleContentDisplay;
